import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

const AUTO_DISMISS_MS = 6000;

/**
 * Compact "added to cart" popup, shown on every successful add-to-cart.
 *
 * It listens for the theme's cart:update event and only reacts to real adds
 * coming from product-form-component (quick-add, PDP and the inline size
 * selector all go through it), so it never fires on cart quantity edits.
 *
 * @typedef {object} CartNotificationRefs
 * @property {HTMLTemplateElement} [moneyFormat] - Holds the shop money format template.
 * @property {HTMLImageElement} [image] - The product thumbnail.
 * @property {HTMLElement} [title] - The product title.
 * @property {HTMLElement} [options] - The selected variant options.
 * @property {HTMLElement} [price] - The formatted price.
 * @extends Component<CartNotificationRefs>
 */
class CartNotification extends Component {
  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    clearTimeout(this.#timer);
  }

  /** @param {Event} [event] */
  close(event) {
    event?.preventDefault();
    this.removeAttribute('open');
    clearTimeout(this.#timer);
  }

  open() {
    this.setAttribute('open', '');
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.close(), AUTO_DISMISS_MS);
  }

  /** @param {CustomEvent} event */
  #onCartUpdate = (event) => {
    const detail = event.detail ?? {};
    const data = detail.data ?? {};
    if (data.didError || data.source !== 'product-form-component') return;

    const cart = detail.resource;
    const variantId = detail.sourceId;
    const item = cart?.items?.find((line) => String(line.variant_id) === String(variantId));
    if (!item) return;

    this.#render(item, cart);
    this.open();
  };

  /**
   * @param {any} item
   * @param {any} cart
   */
  #render(item, cart) {
    const { image, title, options, price } = this.refs;

    if (image) {
      if (item.image) {
        image.src = this.#imageUrl(item.image, 200);
        image.alt = item.product_title ?? item.title ?? '';
        image.hidden = false;
      } else {
        image.hidden = true;
      }
    }

    if (title) title.textContent = item.product_title ?? item.title ?? '';

    if (options) {
      options.textContent = this.#sizeLabel(item);
    }

    if (price) {
      const format = this.refs.moneyFormat?.content?.textContent?.trim() || '{{amount}}';
      const currency = cart?.currency || 'USD';
      price.textContent = formatMoney(item.final_price ?? item.price, format, currency);
    }
  }

  /**
   * @param {string} url
   * @param {number} width
   * @returns {string}
   */
  #imageUrl(url, width) {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const targetWidth = Math.round(width * dpr);
    return url.includes('?') ? `${url}&width=${targetWidth}` : `${url}?width=${targetWidth}`;
  }

  /**
   * Returns only the size option of the added variant, e.g. "Size: L".
   * Falls back to the single varying option when the size name is not recognized.
   * @param {any} item
   * @returns {string}
   */
  #sizeLabel(item) {
    const sizeNames = ['size', 'talla', 'tamaño', 'tamanho', 'taille', 'größe', 'grootte'];
    const options = Array.isArray(item.options_with_values)
      ? item.options_with_values.filter((opt) => opt.value && opt.value !== 'Default Title')
      : [];
    if (options.length === 0) return '';

    const sizeOption =
      options.find((opt) => sizeNames.includes(String(opt.name).trim().toLowerCase())) ||
      (options.length === 1 ? options[0] : null);

    return sizeOption ? `${sizeOption.name}: ${sizeOption.value}` : '';
  }
}

if (!customElements.get('cart-notification')) {
  customElements.define('cart-notification', CartNotification);
}
